/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import ReactSelect, { GroupBase, Props as ReactSelectProps, StylesConfig } from "react-select";
import SvgIcon from "./SvgIcon";

export type SelectProps<Option, IsMulti, Group> = ReactSelectProps<Option, IsMulti, Group> & {
  label?: string;
  info?: string;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  containerStyle?: React.CSSProperties;
};

const colourStyles: StylesConfig<unknown, true> = {
  multiValue: (styles) => {
    return {
      ...styles,
      color: "white",
      backgroundColor: "#00897b"
    };
  },
  multiValueLabel: (styles) => ({
    ...styles,
    color: "white"
  })
};

const Select = React.forwardRef(
  <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: SelectProps<Option, IsMulti, Group>,
    ref: ((instance: ReactSelect<Option, IsMulti, Group> | null) => void) | React.MutableRefObject<Select<Option, IsMulti, Group> | null> | null
  ) => {
    const { label, info, required, disabled, errorMessage, containerStyle, ...rest } = props;
    const invalid = !!errorMessage;

    return (
      <div className={`mb-3 ${invalid && "field-group--invalid"}`} style={containerStyle}>
        {label && <label className="label">{label}</label>}
        {required && <sup style={{ color: "red" }}>*</sup>}
        {info && (
          <span
            className="help"
            data-bs-toggle="tooltip"
            data-trigger="hover"
            data-placement="right"
            data-html="true"
            data-container="body"
            title={info}
          >
            <SvgIcon name="question-circle" className="ml-1 label" />
          </span>
        )}
        <ReactSelect
          isSearchable
          isClearable
          isDisabled={disabled}
          theme={(theme) => ({
            ...theme,
            borderRadius: 7,
            colors: {
              ...theme.colors,
              primary50: "#00897b50",
              primary25: "#00897b25",
              primary: "#00897b"
            },
            spacing: { baseUnit: 5.5 }
          })}
          styles={colourStyles}
          ref={ref}
          {...rest}
        />
        {invalid && (
          <div className="field-helper">
            <label>{errorMessage}</label>
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";

export default Select;
